import {
  connectorTypeOf,
  connectorTypes,
  resolveConnectorDefinition,
} from "../../../shared/connectors";
import { isConnectorAvailable } from "../../../shared/connectors/isConnectorAvailable";
import type { AppTag } from "../../../shared/connectors/tags";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { config } from "../../environment";

export type ConnectorConfig = {
  name: string;
  identifier: string;
  tagLine: string;
  tags: AppTag[];
  definition: ConnectorDefinition;
};

const oauthClientIdentifiers = new Set(
  Object.keys(config.managedOAuthClientIds)
);
const isAvailable = (definition: ConnectorDefinition) =>
  isConnectorAvailable(definition, oauthClientIdentifiers);

const toConfig = (definition: ConnectorDefinition): ConnectorConfig => ({
  name: definition.name,
  identifier: definition.identifier,
  tagLine: definition.tagLine,
  tags: definition.tags,
  definition,
});

const availableVariants = new Map<string, ConnectorConfig[]>();
for (const type of connectorTypes) {
  const variants = type.variants.filter(isAvailable).map(toConfig);
  if (variants.length > 0) availableVariants.set(type.identifier, variants);
}

const connectorNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const connectorDirectory: ConnectorConfig[] = [
  ...availableVariants.values(),
]
  .map((variants) => variants[0]!)
  .sort((a, b) => connectorNameCollator.compare(a.name, b.name));

export const listedConnectorDirectory = connectorDirectory.filter(
  (app) => !app.definition.unlisted
);

export const managedConnectorDirectory = connectorDirectory.filter((app) =>
  app.tags.includes("managed")
);

// The custom connector types worth surfacing by default in the quick-create
// pickers, most-reached-for first. The niche remainder (login credentials,
// SMTP, LDAP) stays creatable from the connectors directory’s “Custom”
// category rather than crowding these menus.
const recommendedCustomConnectorIds = [
  "apiKey",
  "customOauth",
  "remoteMcp",
  "workflow",
];

export const recommendedCustomConnectors: ConnectorConfig[] =
  recommendedCustomConnectorIds
    .map((id) => connectorDirectory.find((app) => app.identifier === id))
    .filter((app): app is ConnectorConfig => app !== undefined);

export function connectorVariants(config: ConnectorConfig): ConnectorConfig[] {
  const type = connectorTypeOf(config.identifier);
  return (type && availableVariants.get(type.identifier)) ?? [];
}

const allConfigs = [...availableVariants.values()].flat();

export function resolveConnectorConfig(
  identifier: string | undefined | null
): ConnectorConfig | undefined {
  const definition = resolveConnectorDefinition(identifier);
  return allConfigs.find((app) => app.identifier === definition?.identifier);
}
